
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { ChevronLeft, ChevronRight, Menu } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Logo } from "@/components/navigation/Logo";
import { SideNav } from "@/components/navigation/SideNav";

export function SidebarLayout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth/signin");
  };

  const toggleSidebar = () => {
    setCollapsed(!collapsed);
  };

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      {/* Desktop sidebar */}
      <div
        className={cn(
          "hidden md:flex flex-col bg-white border-r border-gray-200 transition-all duration-300 ease-in-out",
          collapsed ? "w-20" : "w-64"
        )}
      >
        <div className="p-4 flex justify-between items-center">
          {!collapsed && <Logo className="h-8 w-auto" />}
          <Button
            variant="ghost"
            size="sm"
            className="p-1"
            onClick={toggleSidebar}
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </Button>
        </div>
        <SideNav collapsed={collapsed} />
        <div className="mt-auto p-4">
          <Button
            variant="ghost"
            onClick={handleSignOut}
            className="w-full justify-start text-gray-500 hover:text-gray-700"
          >
            {collapsed ? (
              <span className="mx-auto">➡</span>
            ) : (
              <span>Sign out</span>
            )}
          </Button>
        </div>
      </div>

      {/* Mobile sidebar */}
      <div
        className={cn(
          "fixed inset-0 bg-gray-600 bg-opacity-75 z-20 md:hidden transition-opacity duration-300 ease-in-out",
          mobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={() => setMobileMenuOpen(false)}
      />

      <div
        className={cn(
          "fixed inset-y-0 left-0 flex flex-col bg-white w-64 z-30 md:hidden transition-transform duration-300 ease-in-out transform",
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="p-4 flex justify-between items-center border-b border-gray-200">
          <Logo className="h-8 w-auto" />
          <Button
            variant="ghost"
            size="sm"
            className="p-1"
            onClick={() => setMobileMenuOpen(false)}
          >
            <ChevronLeft size={18} />
          </Button>
        </div>
        <SideNav collapsed={false} />
        <div className="mt-auto p-4">
          <Button
            variant="ghost"
            onClick={handleSignOut}
            className="w-full justify-start text-gray-500 hover:text-gray-700"
          >
            Sign out
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        <header className="bg-white border-b border-gray-200 p-4 flex md:hidden items-center justify-between">
          <Logo className="h-8 w-auto" />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileMenuOpen(true)}
          >
            <Menu size={24} />
          </Button>
        </header>
        <main className="flex-1 overflow-auto">
          <div className="py-6 px-4 sm:px-6 lg:px-8">{children}</div>
        </main>
      </div>
    </div>
  );
}
